import { useQueryClient } from '@tanstack/react-query'
import { useSocketEvent } from '@/shared/lib/socket/hooks'
import { NotificationSocketPayload, SOCKET_NAMESPACES } from '@/shared/lib/socket/types'
import { sonnerToast } from '@workspace/ui/components/Sonner'
import { NotificationToast } from './NotificationToast'

/**
 * NotificationSocketListener - Global socket event listener for real-time notifications
 *
 * This component listens to socket events on the '/notifications' namespace:
 * - Displaying custom toast notifications when new notifications arrive
 * - Refreshing the notification list in React Query cache
 *
 * Backend emits: client.emit('notification', { notification: data.notification })
 *
 * @component
 * @example
 * // Place this at the root level of your app (inside SocketProvider)
 * <NotificationSocketListener />
 */
export function NotificationSocketListener() {
    const queryClient = useQueryClient()

    // Listen for new notifications from backend on /notifications namespace
    // Event name: 'notification'
    // Payload: { notification: Notification }
    useSocketEvent<'notification'>(
        'notification',
        (payload: NotificationSocketPayload) => {
            try {
                const notification = payload.notification

                if (!notification) {
                    console.warn('Received empty notification payload')
                    return
                }
                console.log('Received notification payload:', notification)

                sonnerToast.custom(id => <NotificationToast id={id} notification={notification} />, {
                    position: 'top-right',
                    duration: 3000,
                })

                queryClient.invalidateQueries({
                    queryKey: ['notifications'],
                })
            } catch (error) {
                console.error('Error handling notification event:', error)
            }
        },
        [],
        SOCKET_NAMESPACES.NOTIFICATIONS,
    )

    return null
}
